import { useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  endOfMonth,
  endOfYear,
  format,
  parseISO,
  startOfMonth,
  startOfYear,
  subMonths,
} from "date-fns";
import {
  ArrowLeft,
  BarChart3,
  Building2,
  CircleDollarSign,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useClientArMovements, useClients } from "@/features/clients/api";
import { CLIENT_TYPES, formatMoney, labelFor } from "@/features/clients/constants";
import { cn } from "@/lib/utils";

type Period = "mtd" | "ytd" | "custom";

const CHART_COLORS = ["#3b82f6", "#0ea5e9", "#6366f1", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6"];

export function ClientsAnalyticsPage() {
  return (
    <ErrorBoundary title="Clients analytics crashed">
      <ClientsAnalyticsInner />
    </ErrorBoundary>
  );
}

function ClientsAnalyticsInner() {
  const today = new Date();
  const [period, setPeriod] = useState<Period>("mtd");
  const [customFrom, setCustomFrom] = useState(format(startOfMonth(subMonths(today, 2)), "yyyy-MM-dd"));
  const [customTo, setCustomTo] = useState(format(today, "yyyy-MM-dd"));

  const range = useMemo(() => {
    if (period === "mtd") {
      return {
        from: format(startOfMonth(today), "yyyy-MM-dd"),
        to: format(endOfMonth(today), "yyyy-MM-dd"),
        label: `MTD · ${format(today, "MMM yyyy")}`,
      };
    }
    if (period === "ytd") {
      return {
        from: format(startOfYear(today), "yyyy-MM-dd"),
        to: format(endOfYear(today), "yyyy-MM-dd"),
        label: `YTD · ${format(today, "yyyy")}`,
      };
    }
    return {
      from: customFrom,
      to: customTo,
      label: `${format(parseISO(customFrom), "dd MMM yyyy")} – ${format(parseISO(customTo), "dd MMM yyyy")}`,
    };
  }, [period, customFrom, customTo, today]);

  const { data: clients = [], isLoading: loadingClients } = useClients();
  // Fetch a wider window so monthly trend charts have history (from FY start / 6 months)
  const historyFrom = format(startOfYear(today), "yyyy-MM-dd");
  const { data: movements = [], isLoading: loadingMoves } = useClientArMovements({
    from: historyFrom,
    to: format(today, "yyyy-MM-dd"),
  });

  const loading = loadingClients || loadingMoves;

  const stats = useMemo(() => {
    const active = clients.filter((c) => c.is_active);
    const toCollect = clients.reduce((s, c) => s + (c.outstanding_balance || 0), 0);
    const overdue = clients.reduce((s, c) => s + (c.overdue_balance || 0), 0);
    const opening = clients.reduce((s, c) => s + (c.opening_balance || 0), 0);

    const inRange = movements.filter((m) => m.movement_date >= range.from && m.movement_date <= range.to);
    const invoiced = inRange
      .filter((m) => m.kind === "invoice")
      .reduce((s, m) => s + m.amount, 0);
    const collected = inRange
      .filter((m) => m.kind === "receipt" || m.kind === "credit_note")
      .reduce((s, m) => s + Math.abs(m.amount), 0);

    return {
      total: clients.length,
      active: active.length,
      inactive: clients.length - active.length,
      toCollect,
      overdue,
      current: Math.max(0, toCollect - overdue),
      opening,
      invoiced,
      collected,
    };
  }, [clients, movements, range.from, range.to]);

  const monthlyTrend = useMemo(() => {
    const buckets = new Map<string, { month: string; invoiced: number; collected: number; net: number }>();
    for (const m of movements) {
      if (m.kind === "opening") continue;
      const key = m.movement_date.slice(0, 7);
      const row = buckets.get(key) ?? { month: key, invoiced: 0, collected: 0, net: 0 };
      if (m.kind === "invoice") row.invoiced += m.amount;
      if (m.kind === "receipt" || m.kind === "credit_note") row.collected += Math.abs(m.amount);
      row.net = row.invoiced - row.collected;
      buckets.set(key, row);
    }
    return [...buckets.values()]
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((r) => ({
        ...r,
        label: format(parseISO(`${r.month}-01`), "MMM yy"),
      }));
  }, [movements]);

  const dueByClient = useMemo(() => {
    return [...clients]
      .filter((c) => c.outstanding_balance > 0)
      .sort((a, b) => b.outstanding_balance - a.outstanding_balance)
      .slice(0, 8)
      .map((c) => ({
        name: c.name.length > 18 ? `${c.name.slice(0, 16)}…` : c.name,
        fullName: c.name,
        due: c.outstanding_balance,
        overdue: c.overdue_balance,
      }));
  }, [clients]);

  const typeMix = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of clients) {
      map.set(c.client_type, (map.get(c.client_type) ?? 0) + 1);
    }
    return [...map.entries()].map(([type, count]) => ({
      name: labelFor(CLIENT_TYPES, type),
      value: count,
    }));
  }, [clients]);

  const aging = useMemo(() => {
    const current = stats.current;
    const overdue = stats.overdue;
    // Split overdue into rough buckets for visual (demo until invoice due dates exist)
    return [
      { name: "Current", value: current, fill: "#3b82f6" },
      { name: "1–30 overdue", value: Math.round(overdue * 0.45), fill: "#f59e0b" },
      { name: "31–60 overdue", value: Math.round(overdue * 0.35), fill: "#f97316" },
      { name: "60+ overdue", value: Math.round(overdue * 0.2), fill: "#ef4444" },
    ].filter((b) => b.value > 0);
  }, [stats.current, stats.overdue]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Button asChild variant="ghost" size="icon-sm" aria-label="Back to clients">
            <Link to="/clients">
              <ArrowLeft />
            </Link>
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold tracking-[-0.01em]">
              <BarChart3 className="size-5 text-primary" />
              Clients analytics
            </h1>
            <p className="text-xs text-muted-foreground">
              Due amounts, collections and mix across the client book · {range.label}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex h-8 items-center rounded-md border border-border bg-muted/50 p-0.5">
            {(
              [
                { value: "mtd", label: "MTD" },
                { value: "ytd", label: "YTD" },
                { value: "custom", label: "Custom" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPeriod(opt.value)}
                className={cn(
                  "h-7 rounded px-2.5 text-xs transition-colors",
                  period === opt.value
                    ? "bg-card font-medium text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {period === "custom" ? (
            <>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-36"
                aria-label="From date"
              />
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-36"
                aria-label="To date"
              />
            </>
          ) : null}
        </div>
      </header>

      {loading ? (
        <p className="py-16 text-center text-sm text-muted-foreground">Loading analytics…</p>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            <Kpi icon={<Users />} label="Total clients" value={String(stats.total)} />
            <Kpi icon={<Building2 />} label="Active" value={String(stats.active)} />
            <Kpi
              icon={<CircleDollarSign />}
              label="To collect"
              value={formatMoney(stats.toCollect)}
            />
            <Kpi
              icon={<TrendingDown />}
              label="Overdue"
              value={formatMoney(stats.overdue)}
              tone="danger"
            />
            <Kpi
              icon={<TrendingUp />}
              label={`Invoiced (${period.toUpperCase()})`}
              value={formatMoney(stats.invoiced)}
            />
            <Kpi
              icon={<TrendingUp />}
              label={`Collected (${period.toUpperCase()})`}
              value={formatMoney(stats.collected)}
              tone="success"
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard title="Invoiced vs collected" subtitle="Monthly trend from AR movements">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="inv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="col" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="var(--muted-foreground)"
                      tickFormatter={(v) => compactInr(v)}
                    />
                    <Tooltip content={<MoneyTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area
                      type="monotone"
                      dataKey="invoiced"
                      name="Invoiced"
                      stroke="#3b82f6"
                      fill="url(#inv)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="collected"
                      name="Collected"
                      stroke="#14b8a6"
                      fill="url(#col)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Due by client" subtitle="Top outstanding balances">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dueByClient}
                    layout="vertical"
                    margin={{ top: 4, right: 12, left: 4, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11 }}
                      stroke="var(--muted-foreground)"
                      tickFormatter={(v) => compactInr(v)}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={110}
                      tick={{ fontSize: 11 }}
                      stroke="var(--muted-foreground)"
                    />
                    <Tooltip content={<MoneyTooltip />} />
                    <Bar dataKey="due" name="To collect" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="overdue" name="Overdue" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Client type mix" subtitle="Count of clients by type">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={typeMix}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={84}
                      paddingAngle={2}
                    >
                      {typeMix.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard title="Aging of dues" subtitle="Current vs overdue buckets">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aging} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="var(--muted-foreground)"
                      tickFormatter={(v) => compactInr(v)}
                    />
                    <Tooltip content={<MoneyTooltip />} />
                    <Bar dataKey="value" name="Amount" radius={[4, 4, 0, 0]}>
                      {aging.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          <ChartCard
            title="Opening balances"
            subtitle="Brought-forward AR with cut-off date — ledger entries after this date must reconcile"
          >
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
                    <th className="px-2 py-2 font-semibold">Client</th>
                    <th className="px-2 py-2 font-semibold">As of</th>
                    <th className="px-2 py-2 text-right font-semibold">Opening</th>
                    <th className="px-2 py-2 text-right font-semibold">To collect</th>
                    <th className="px-2 py-2 text-right font-semibold">Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} className="border-b border-border/70">
                      <td className="px-2 py-1.5">
                        <Link
                          to="/clients/$clientId/profile"
                          params={{ clientId: c.id }}
                          className="font-medium hover:text-primary"
                        >
                          {c.name}
                        </Link>
                        <div className="font-mono text-2xs text-muted-foreground">{c.code}</div>
                      </td>
                      <td className="px-2 py-1.5 text-xs text-muted-foreground">
                        {c.opening_balance_as_of
                          ? format(parseISO(c.opening_balance_as_of), "dd MMM yyyy")
                          : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular text-xs">
                        {c.opening_balance ? formatMoney(c.opening_balance) : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular text-xs font-medium">
                        {c.outstanding_balance ? formatMoney(c.outstanding_balance) : "—"}
                      </td>
                      <td
                        className={cn(
                          "px-2 py-1.5 text-right tabular text-xs",
                          c.overdue_balance > 0 && "font-medium text-destructive",
                        )}
                      >
                        {c.overdue_balance ? formatMoney(c.overdue_balance) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  icon,
  tone = "neutral",
}: {
  label: string;
  value: string;
  icon?: ReactNode;
  tone?: "neutral" | "danger" | "success";
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="field-label">{label}</p>
        {icon ? <span className="text-muted-foreground/60 [&_svg]:size-3.5">{icon}</span> : null}
      </div>
      <p
        className={cn(
          "mt-0.5 text-base font-semibold tabular tracking-[-0.02em]",
          tone === "danger" && "text-destructive",
          tone === "success" && "text-success",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-3 md:p-4">
      <header className="mb-3">
        <h2 className="text-xs font-semibold">{title}</h2>
        {subtitle ? <p className="text-2xs text-muted-foreground">{subtitle}</p> : null}
      </header>
      {children}
    </section>
  );
}

function MoneyTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      {label ? <p className="mb-1 font-medium">{label}</p> : null}
      {payload.map((p) => (
        <p key={p.name} className="flex items-center gap-2 tabular">
          <span className="size-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="font-medium">{formatMoney(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

function compactInr(value: number) {
  if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
  return String(value);
}
